import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import { ArrowIcon } from "../components/Icons";
import PageLoader from "../components/PageLoader";
import { ProfilePageTypes } from "../types";
import Billing from "./Billing";
import Settings from "./Settings";
import Generations from "./Generations";
import { GenerationsProvider } from "../contexts/GenerationsContext";
import APIKeys from "./APIKeys";
import BlenderPlugin from "./BlenderPlugin";
import { Button } from "../components/ui/button";

function ProfilePage() {
  const [searchParams] = useSearchParams();
  const requestedTab =
    searchParams.get("tab")?.toUpperCase() === "BILLING"
      ? "BILLING"
      : "PROFILE";
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedPage, setSelectedPage] =
    useState<ProfilePageTypes>(requestedTab);

  const { currentUser, userLoading } = useAuth();

  const sidebarWidth = sidebarOpen ? 400 : 20;

  useEffect(() => {
    if (searchParams.get("tab")?.toUpperCase() === "BILLING") {
      setSelectedPage("BILLING");
    }
  }, [searchParams]);

  if (userLoading) {
    return <PageLoader />;
  } else if (currentUser == null) {
    window.location.href = "/";
    return null;
  }

  return (
    <div className="relative w-full h-screen max-h-screen flex flex-col bg-background overflow-hidden">
      <GenerationsProvider>
        <Header />
        {/* Arrow icon */}
        <div
          className="rounded-full fixed top-28 z-20 w-8 h-8 flex justify-center items-center cursor-pointer bg-background sidebar-backdrop border-2 border-border neo-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
          style={{ left: sidebarOpen ? "384px" : "6px" }}
          onClick={() => {
            setSidebarOpen(!sidebarOpen);
          }}
        >
          <ArrowIcon
            className={`text-foreground ${sidebarOpen ? "" : "rotate-180"}`}
          />
        </div>

        <div className="relative h-full flex-1 flex overflow-hidden">
          <div
            className={`bg-background sidebar-backdrop border-2 border-border h-full flex flex-col transition-all overflow-hidden neo-shadow absolute left-0 z-10`}
            style={{
              maxWidth: `${sidebarWidth}px`,
              minWidth: `${sidebarWidth}px`,
            }}
          >
            <div
              className="text-center flex-1 transition-all"
              style={{ opacity: sidebarOpen ? 1 : 0 }}
            >
              <ul className="w-full text-foreground text-lg text-left">
                <TabLink
                  name="PROFILE"
                  selectedPage={selectedPage}
                  setSelectedPage={setSelectedPage}
                >
                  Generations
                </TabLink>

                <TabLink
                  name="BLENDER_PLUGIN"
                  selectedPage={selectedPage}
                  setSelectedPage={setSelectedPage}
                >
                  Blender Plugin
                </TabLink>

                <TabLink
                  name="API_KEYS"
                  selectedPage={selectedPage}
                  setSelectedPage={setSelectedPage}
                >
                  API Keys
                </TabLink>

                <TabLink
                  name="BILLING"
                  selectedPage={selectedPage}
                  setSelectedPage={setSelectedPage}
                >
                  Billing
                </TabLink>

                <TabLink
                  name="SETTINGS"
                  selectedPage={selectedPage}
                  setSelectedPage={setSelectedPage}
                >
                  Settings
                </TabLink>
              </ul>
            </div>

            {currentUser && (
              <div className="border-t-2 border-border py-10 flex justify-center">
                <Button asChild>
                  <a href="/">New Generation</a>
                </Button>
              </div>
            )}
          </div>

          <div
            style={{ paddingLeft: `${sidebarWidth}px` }}
            className="transition-all overflow-y-auto h-full flex-1"
          >
            {selectedPage === "BILLING" && <Billing />}
            {selectedPage === "SETTINGS" && <Settings />}
            {selectedPage === "BLENDER_PLUGIN" && <BlenderPlugin />}
            {selectedPage === "PROFILE" && <Generations />}
            {selectedPage === "API_KEYS" && <APIKeys />}
          </div>
        </div>
      </GenerationsProvider>
    </div>
  );
}

const TabLink: React.FC<{
  name: ProfilePageTypes;
  children: React.ReactNode;
  selectedPage: ProfilePageTypes;
  setSelectedPage: (page: ProfilePageTypes) => void;
}> = ({ name, children, selectedPage, setSelectedPage }) => {
  return (
    <li
      onClick={() => setSelectedPage(name)}
      className={clsx(
        "w-full py-6 cursor-pointer my-1 px-10 border-2 border-border neo-shadow text-foreground neo-surface hover:translate-x-[4px] hover:translate-y-[4px] hover:shadow-none transition-all",
        selectedPage === name
          ? "bg-main text-[color:var(--color-main-foreground)]"
          : "",
      )}
    >
      {children}
    </li>
  );
};

export default ProfilePage;
